import { assertErrors, fetchAPI, METHODS, parseBody } from './common';
import qs from 'qs';

/**
 * Fetches all organizations within the account's geo-fence.
 * @param {object} params0
 * @param {string[]} params0.geofences List of geofence ids.
 * @param {import('./rescues').FilterModelObj} params0.filterModel MUI-DataGrid Filter model to apply to result set
 * @param {import('./donations').PageState} params0.pageState Pagination settings for results
 * @returns Organization[]
 */
export async function getAllOrganizations({
  geofences,
  filterModel,
  pageState,
  showInactive = false,
}) {
  const res = await fetchAPI(
    `/executive/organizations?${qs.stringify(pageState)}`,
    METHODS.post,
    {
      filterModel,
      geofences,
      showInactive,
    }
  );
  return parseBody(res);
}

/**
 * Fetches all organizations within the account's geo-fence.
 * @param {number[]} geofences List of geofence ids.
 * @returns Organization[]
 */
export async function getOrganization(organizationId) {
  const res = await fetchAPI(
    `/executive/organizations/${organizationId}`,
    METHODS.get
  );
  const { organization } = await parseBody(res);
  return organization;
}

/**
 * Fetches an organization by its team id (not org id like getOrganization)
 * @param {string} teamId ID of the team
 * @returns {Promise<Organization>}
 */
export async function getOrganizationByTeamId(teamId) {
  const res = await fetchAPI(
    `/executive/organization/team-id/${teamId}`,
    METHODS.get
  );
  const { organization } = await parseBody(res);
  return organization;
}

/**
 * Get top donors located in the selected geofences
 * @param {array} geofences
 * @returns DonorsResult
 */
export async function getTopDonors({ geofences }) {
  const res = await fetchAPI(`/executive/top-donors`, METHODS.post, {
    geofences,
  });

  const { donors } = await parseBody(res);
  return donors;
}

/**
 * Fetches all geozones assigned to the executive user.
 * @returns Geofence[]
 */
export async function getAllGeofences() {
  const res = await fetchAPI('/executive/geozones', METHODS.get);
  const { geozones } = await parseBody(res);
  return geozones;
}

/**
 * Rename a geofence.
 * @param {string} id
 * @param {string} name
 */
export async function renameGeofence(id, name) {
  await fetchAPI(`/executive/geozones/${id}`, METHODS.put, {
    name,
  });
}

/**
 * Delete a geofence.
 * @param {string} id
 */
export async function deleteGeofence(id) {
  await fetchAPI(`/executive/geozones/${id}`, METHODS.delete);
}

/**
 * Update the executive organization's settings.
 * @param {string} param0.name
 * @param {string} param0.email
 * @param {string} param0.about
 */
export async function updateSettings({
  name,
  email,
  description,
  country,
  province,
  timeZoneId,
}) {
  const res = await fetchAPI('/executive/settings/organization', METHODS.put, {
    name,
    email,
    description,
    country,
    province,
    timeZoneId,
  });
  await assertErrors(res);
}

/**
 * @typedef Note
 * @property {string} id
 * @property {string} text
 * @property {Date} created
 */

/**
 * Get all of the notes for an organization with id `team_id`.
 * @param {string} teamId
 * @returns Promise<Note>
 */
export async function getNotes(teamId) {
  const res = await fetchAPI(`/executive/notes/${teamId}`, METHODS.get);
  const { notes } = await parseBody(res);
  return notes;
}

/**
 * Add a note on an organization with id `team_id`.
 * @param {string} teamId
 * @param {string} text Note text, max 500 chars.
 */
export async function addNote(teamId, text) {
  await fetchAPI(`/executive/note/${teamId}`, METHODS.post, { text });
}

/**
 * @typedef Tag
 * @property {string} id
 * @property {string} name
 */

/**
 * Get all available tags owned by an organization.
 * @returns Promise<Tag[]>
 */
export async function getTags() {
  const res = await fetchAPI('/executive/tags', METHODS.get);
  const { tags } = await parseBody(res);
  return tags;
}

/**
 * Create a new tag for the user's organization.
 * The user's organization is henceforth the "owning organization."
 * @param {string} name Name of the tag.
 * @returns string ID of the new tag
 */
export async function createTag(name) {
  const res = await fetchAPI('/executive/tags', METHODS.post, { name });
  const { id } = await parseBody(res);
  return id;
}

/**
 * Get all tags (belonging to the executive organization) attached to a specific
 * organization.
 * @param {string} teamId ID of the attached team.
 * @returns Promise<Tag[]>
 */
export async function getTagsFromOrg(teamId) {
  const res = await fetchAPI(`/executive/tags/${teamId}`, METHODS.get);
  const { tags } = await parseBody(res);
  return tags;
}

/**
 * Attach a pre-exiting tag to an organization.
 * It is assumed that the `id` of the tag is valid and under ownership of the
 * executive user.
 * @param {string} id ID of the tag to attach
 * @param {string} teamId ID of the team of which is being tagged.
 */
export async function addTagToOrg(id, teamId) {
  await fetchAPI(`/executive/tags/${teamId}`, METHODS.post, { id });
}

/**
 * Remove or un-attach a tag from an organization.
 * @param {string} id ID of the tag.
 * @param {string} teamId ID of the team of which the tag is attached.
 */
export async function deleteTagFromOrg(id, teamId) {
  await fetchAPI(`/executive/tags/${teamId}/${id}`, METHODS.delete);
}

/**
 * Get all agreements concerning the organization of `teamId`.
 * @param {string} teamId
 * @returns Promise<Agreement[]>
 */
export async function getAgreementsForOrg(teamId) {
  const res = await fetchAPI(`/executive/agreements/${teamId}`, METHODS.get);
  const { agreements } = await parseBody(res);
  return agreements;
}

/**
 * Get a specific inspection by `id`.
 * @param {string} id
 * @param {"auto" | string | null} type
 * @returns Promise<Inspection | null>
 */
export async function getInspectionById(id, type = null) {
  const res = await fetchAPI(
    `/executive/inspection/${id}${type ? `?${qs.stringify({ type })}` : ''}`,
    METHODS.get
  );
  const { inspection } = await parseBody(res);
  return inspection;
}

/**
 * Get all inspections (created by the currect executive org).
 * @params {boolean | null} query.isComplete
 * @params {number | null} query.offset
 * @params {number | null} query.step
 * @params {FilterModel} filterModel
 * @params {string[]} geofences Ids of active geofences
 * @returns Promise<Inspection[]>
 */
export async function getAllInspections(query, filterModel, geofences) {
  const res = await fetchAPI(
    `/executive/inspections?${qs.stringify(query)}`,
    METHODS.post,
    { filterModel, geofences }
  );
  const { inspections } = await parseBody(res);
  return inspections;
}

/**
 * Get a list of inspections for a given inspector.
 * @params {string} userId
 * @params {boolean | null} query.isComplete
 * @params {number | null} query.offset
 * @params {number | null} query.step
 * @params {FilterModel} filterModel
 * @params {string[]} geofences Ids of active geofences
 * @returns Promise<Inspection[]>
 */
export async function getInspectionsForInspector(
  userId,
  query,
  filterModel,
  geofences
) {
  const res = await fetchAPI(
    `/executive/inspections/inspector/${userId}?${qs.stringify(query)}`,
    METHODS.post,
    { filterModel, geofences }
  );
  const { inspections } = await parseBody(res);
  return inspections;
}

/**
 * Get a list of inspections for a given team.
 * @params {string} teamId
 * @params {boolean | null} query.isComplete
 * @params {number | null} query.offset
 * @params {number | null} query.step
 * @params {FilterModel} filterModel
 * @params {string[]} geofences Ids of active geofences
 * @returns Promise<Inspection[]>
 */
export async function getInspectionsForTeam(
  teamId,
  query,
  filterModel,
  geofences
) {
  const res = await fetchAPI(
    `/executive/inspections/organization/${teamId}?${qs.stringify(query)}`,
    METHODS.post,
    { filterModel, geofences }
  );
  const { inspections } = await parseBody(res);
  return inspections;
}

/**
 * Create a new inspection of an inspectee with data (js object)
 * @param {string} inspecteeTeamId
 * @param {string} type
 * @param {object} data
 * @param {boolean} isComplete
 */
export async function createInspection(
  inspecteeTeamId,
  type,
  data,
  isComplete
) {
  const res = await fetchAPI('/executive/inspection', METHODS.post, {
    inspecteeTeamId,
    type,
    data,
    isComplete,
  });
  await assertErrors(res);
}

/**
 * Update an existing inspection.
 * @param {string} id
 * @param {object} data
 * @param {boolean} isComplete
 */
export async function updateInspection(id, data, isComplete) {
  const res = await fetchAPI('/executive/inspection', METHODS.put, {
    id,
    data,
    isComplete,
  });
  await assertErrors(res);
}

/**
 * Delete an existing inspection.
 * @param {string} id
 */
export async function deleteInspection(id) {
  const res = await fetchAPI(`/executive/inspection/${id}`, METHODS.delete, {
    id,
  });
  await assertErrors(res);
}

/**
 * Fetches all donations within the account's geo-fence.
 * @param {number[]} param0.geofences List of geofence ids.
 * @param {String} param0.type One of INTERNAL, INCOMING, OUTGOING or null
 * @param {number} param0.step
 * @param {number} param0.offset
 * @param {object} param0.filterModel Data grid column filter
 * @param {String} param0.timeZoneId Timezone Id of the user
 * @returns Donation[]
 */
export async function getAllDonationsPaginated({
  geofences,
  type,
  step = 20,
  offset = 1,
  filterModel,
  timeZoneId,
}) {
  const res = await fetchAPI('/executive/donations/paginated', METHODS.post, {
    geofences,
    type,
    step,
    offset,
    filterModel,
    timeZoneId,
  });
  const { donations, count, totalWeight, totalsPerFoodType } = await parseBody(
    res
  );
  return { donations, count, totalWeight, totalsPerFoodType };
}

/**
 * Download all donations within the account's geo-fence as csv.
 * @param {number[]} param0.geofences List of geofence ids.
 * @param {String} param0.type One of INTERNAL, INCOMING, OUTGOING or null
 * @param {object} param0.filterModel Data grid column filter
 * @param {string} param0.timeZoneId Timezone Id of the user
 * @param {string} param0.visibleColumns visible columns in the data grid
 * @returns File
 */
export async function downloadDonationsGeofencedCsv({
  geofences,
  type,
  filterModel,
  timeZoneId,
  visibleColumns,
}) {
  const res = await fetchAPI('/executive/donations/csv', METHODS.post, {
    geofences,
    type,
    filterModel,
    timeZoneId,
    visibleColumns,
  });
  const csvBlob = await res.blob();
  const url = window.URL.createObjectURL(csvBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'donations.csv');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Cleanup the DOM
  document.body.removeChild(link);
}

/**
 * Fetches the contact types from the executive contact log.
 *
 * @async
 * @function fetchContactTypes
 * @returns {Promise<Array<{id: number, name: 'phone' | 'email' | 'mail'}>>} An array of contact type objects containing id and name properties.
 */
export async function fetchContactTypes() {
  const res = await fetchAPI(`/executive/contact-log/types`, METHODS.get);
  const { types } = await parseBody(res);
  return types;
}

/**
 * Adds a contact log entry.
 *
 * @param {Object} params
 * @param {string} params.id - The ID of the contact log.
 * @param {string} params.typeId - The contact type id.
 * @param {string} params.orgTeamId - The organization team ID associated with the contact log.
 * @param {string} params.notes
 * @param {boolean} params.negativeInteraction
 * @param {Date} params.contactedAt - The date and time when the contact was made.
 * @param {Array} params.attachments - Attachments related to the contact log.
 * @returns {Promise<void>}
 */
export async function addContactLog({
  id,
  typeId,
  orgTeamId,
  negativeInteraction,
  notes,
  contactedAt,
  attachments,
}) {
  const res = await fetchAPI(`/executive/contact-log`, METHODS.post, {
    id,
    typeId,
    negativeInteraction,
    orgTeamId,
    notes,
    contactedAt,
    attachments,
  });
  await assertErrors(res);
}

/**
 * Update a contact log entry.
 *
 * @param {Object} params
 * @param {string} params.id - The ID of the contact log.
 * @param {string} params.typeId - The contact type id.
 * @param {string} params.notes
 * @param {boolean} params.negativeInteraction
 * @param {Date} params.contactedAt - The date and time when the contact was made.
 * @param {Array} params.attachments - Attachments related to the contact log.
 * @returns {Promise<void>}
 */
export async function updateContactLog({
  id,
  typeId,
  negativeInteraction,
  notes,
  contactedAt,
  attachments,
}) {
  const res = await fetchAPI(`/executive/contact-log/${id}`, METHODS.put, {
    typeId,
    negativeInteraction,
    notes,
    contactedAt,
    attachments,
  });
  await assertErrors(res);
}

export async function fetchContactLogs(orgTeamId = '') {
  const res = await fetchAPI(
    `/executive/contact-log/${orgTeamId}`,
    METHODS.get
  );
  const { logs } = await parseBody(res);
  return logs;
}

export async function getContactSummary({ geofences, startDate, endDate }) {
  const res = await fetchAPI(`/executive/contact-summary`, METHODS.post, {
    geofences: geofences ?? [],
    startDate,
    endDate,
  });
  const { results } = await parseBody(res);
  return results?.[0];
}

export async function deleteContactLog(id) {
  const res = await fetchAPI(`/executive/contact-log/${id}`, METHODS.delete);
  await assertErrors(res);
}

/**
 * @typedef {Object} NOVRecommended
 * @property {string} id - ID of the inspection
 * @property {string} name - Inspectee Organization Name
 * @property {string} suite_number - Inspectee Suite Number
 * @property {string} street_address - Inspectee Street Address
 * @property {string} city Inspectee - Municipality Name
 * @property {string} province - Inspectee Province/State Name
 * @property {string} zip_code - Inspectee ZIP Code
 * @property {string} created - Inspection creation date
 * @property {string} updated - Inspection last updated date
 * @property {string} inspectorUserId - User ID of the inspector
 * @property {string} inspectorTeamId - Team ID of the inspector
 * @property {string} inspecteeTeamId - Team ID of the inspectee
 * @property {string} type - Type of the inspection
 * @property {string} inspectorName - Name of the inspector
 * @property {string} date_of_inspection - Date the inspection was conducted
 * @property {boolean} compliant - True of the organization is overall compliant
 * @property {string} compliance_date - Date of compliance determination
 * @property {boolean} compliance_lists_fros
 * @property {boolean} compliance_records_fros_food_types
 * @property {boolean} compliance_records_collection_frequency
 * @property {boolean} compliance_records_donations
 * @property {boolean} compliance_donates_maximum
 * @property {boolean} compliance_stores_contracted_fros
 * @property {boolean} compliance_holds_stores_contracts
 * @property {boolean} compliance_nothing_to_donate
 * @property {boolean} compliance_maintains_records_fro
 * @property {boolean} compliance_maintains_records_frs
 * @property {boolean} compliance_tracks_quantities
 */

/**
 * Fetch recommended rows for NOV.
 *
 * @param {Object} params
 * @param {number} params.step - Pagination step value.
 * @param {number} params.offset - Pagination offset value.
 * @param {import('./rescues').FilterModelObj} params.filterModel
 * @returns {Promise<{ results: NOVRecommended[], count: number }>}
 */
export async function getNOVRecommendedRows({ step, offset, filterModel }) {
  const res = await fetchAPI(
    `/executive/nov/recommended/rows?step=${step}&offset=${offset}`,
    METHODS.post,
    { filterModel }
  );
  return parseBody(res);
}

/**
 * Fetch recommended rows for NOV for a specific teamId.
 *
 * @param {Object} params
 * @param {number} params.step - Pagination step value.
 * @param {number} params.offset - Pagination offset value.
 * @param {number} params.teamId - Team ID of team to only select inspections for.
 * @returns {Promise<{ results: NOVRecommended[], count: number }>}
 */
export async function getNOVRecommendedForTeamId({ step, offset, teamId }) {
  const res = await fetchAPI(
    `/executive/nov/recommended/for-team/${teamId}?${qs.stringify({
      step,
      offset,
    })}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Fetch NOV recommended report as CSV.
 *
 * @param {Object} params
 * @param {import('./rescues').FilterModelObj} params.filterModel
 * @returns {Promise<{ results: NOVRecommended[] }>}
 */
export async function getNOVRecommendedCSV({ filterModel }) {
  const res = await fetchAPI(`/executive/nov/recommended/csv`, METHODS.post, {
    filterModel,
  });
  return parseBody(res);
}

/**
 * Fetch issued rows for NOV based on teamId.
 *
 * @param {string} teamId - The ID of the team.
 * @param {Object} params
 * @param {number} params.step - Pagination step value.
 * @param {number} params.offset - Pagination offset value.
 * @param {import('./rescues').FilterModelObj} params.filterModel
 * @returns {Promise<Object>}
 */
export async function getNOVIssuedRowsByTeamId(
  teamId,
  { step, offset, filterModel }
) {
  const res = await fetchAPI(
    `/executive/nov/issued/rows/${teamId}?step=${step}&offset=${offset}`,
    METHODS.post,
    { filterModel }
  );
  return parseBody(res);
}

/**
 * Fetch issued rows for NOV.
 *
 * @param {Object} params
 * @param {number} params.step - Pagination step value.
 * @param {number} params.offset - Pagination offset value.
 * @param {import('./rescues').FilterModelObj} params.filterModel
 * @returns {Promise<Object>}
 */
export async function getNOVIssuedRows({ step, offset, filterModel }) {
  const res = await fetchAPI(
    `/executive/nov/issued/rows?step=${step}&offset=${offset}`,
    METHODS.post,
    { filterModel }
  );
  return parseBody(res);
}

/**
 * Fetch issued report as CSV for a specific team.
 *
 * @param {string} teamId - The ID of the team.
 * @param {Object} params
 * @param {import('./rescues').FilterModelObj} params.filterModel
 * @returns {Promise<string>}
 */
export async function getNOVIssuedCSVByTeamId(teamId, { filterModel }) {
  const res = await fetchAPI(
    `/executive/nov/issued/csv/${teamId}`,
    METHODS.post,
    { filterModel }
  );
  const { id } = await parseBody(res);
  return id;
}

/**
 * Fetch issued report as CSV.
 *
 * @param {Object} params
 * @param {import('./rescues').FilterModelObj} params.filterModel
 * @returns {Promise<Object>}
 */
export async function getNOVIssuedCSV({ filterModel }) {
  const res = await fetchAPI(`/executive/nov/issued/csv`, METHODS.post, {
    filterModel,
  });
  const result = await parseBody(res);
  return result;
}

/**
 * Create a new NOV entry.
 *
 * @param {Object} nov - The NOV data.
 * @param {string} teamId - The ID of the team.
 * @returns {Promise<Object>}
 */
export async function createNOV(nov, teamId) {
  const res = await fetchAPI(`/executive/nov/${teamId}`, METHODS.post, nov);
  const result = await parseBody(res);
  return result;
}

/**
 * Update an existing NOV entry.
 *
 * @param {string} id - The ID of the NOV entry.
 * @param {Object} nov - The updated NOV data.
 * @returns {Promise<Object>}
 */
export async function updateNOV(id, nov) {
  const res = await fetchAPI(`/executive/nov/${id}`, METHODS.put, nov);
  const result = await parseBody(res);
  return result;
}

/**
 * Delete an existing NOV entry.
 *
 * @param {string} id - The ID of the NOV entry.
 * @returns {Promise<Object>}
 */
export async function deleteNOV(id) {
  const res = await fetchAPI(`/executive/nov/${id}`, METHODS.delete);
  const result = await parseBody(res);
  return result;
}

/**
 * Get all inspections with penalty recommendation status for a specific organization
 * @param {string} teamId - The team ID of the organization
 * @returns {Promise<Array>} - Array of inspections with penalty recommendation status
 */
export async function getPenaltyRecommendedInspections(
  teamId,
  limit = 20,
  offset = 0
) {
  const url =
    `/executive/inspections/penalty-recommended/${teamId}` +
    `?limit=${limit}&offset=${offset}`;
  const response = await fetchAPI(url, METHODS.post);
  return await parseBody(response);
}

/**
 * Update penalty recommendation status for an inspection
 * @param {string} inspectionId - The ID of the inspection
 * @param {boolean} recommended - Whether to recommend penalty or not
 * @returns {Promise<void>}
 */
export async function updatePenaltyRecommendation(inspectionId, recommended) {
  const response = await fetchAPI(
    `/executive/inspections/${inspectionId}/penalty-recommendation`,
    METHODS.put,
    { recommended }
  );
  return parseBody(response);
}

/**
 * Get all penalties
 * @param {Object} options - Options for filtering and pagination
 * @param {number} options.page - Page number (0-based)
 * @param {number} options.pageSize - Number of items per page
 * @param {Object} options.filterModel - Filter model for filtering penalties
 * @returns {Promise<{penalties: Array, count: number}>} - Array of penalties with organization and staff member details and total count
 */
export async function getPenalties({
  page = 0,
  pageSize = 25,
  filterModel = null,
} = {}) {
  const response = await fetchAPI(`/executive/penalties`, METHODS.post, {
    page,
    pageSize,
    filterModel,
  });
  return parseBody(response);
}

/**
 * Get penalties for a specific organization
 * @param {string} teamId - The team ID of the organization
 * @param {Object} options - Options for filtering and pagination
 * @param {number} options.page - Page number (0-based)
 * @param {number} options.pageSize - Number of items per page
 * @param {Object} options.filterModel - Filter model for filtering penalties
 * @returns {Promise<{penalties: Array, count: number}>} - Array of penalties for the specified organization and total count
 */
export async function getPenaltiesByOrganization(
  teamId,
  { page = 0, pageSize = 25, filterModel = null } = {}
) {
  const response = await fetchAPI(
    `/executive/penalties/organization/${teamId}?page=${page}&pageSize=${pageSize}`,
    METHODS.post,
    { filterModel }
  );
  return parseBody(response);
}

/**
 * Issue a new penalty
 * @param {Object} data - The penalty data
 * @param {string} data.teamId - The team ID of the organization
 * @param {string} data.inspectionId - The ID of the inspection related to this penalty
 * @param {string} data.dateOfPenalty - The date the penalty was issued (ISO string)
 * @param {string} data.dateForFollowup - The follow-up date for the penalty (ISO string)
 * @returns {Promise<void>}
 */
export async function issuePenalty(data) {
  const response = await fetchAPI(
    '/executive/penalties/issue',
    METHODS.post,
    data
  );
  return parseBody(response);
}

/**
 * Update a penalty
 * @param {string} penaltyId - The ID of the penalty to update
 * @param {Object} data - The data to update
 * @param {Date} data.dateOfPenalty - The new date of penalty
 * @param {Date} data.dateForFollowup - The new date for followup
 * @returns {Promise<void>}
 */
export async function updatePenalty(penaltyId, data) {
  const response = await fetchAPI(
    `/executive/penalties/${penaltyId}`,
    METHODS.put,
    data
  );
  return parseBody(response);
}

/**
 * Delete a penalty
 * @param {string} penaltyId - The ID of the penalty to delete
 * @returns {Promise<void>}
 */
export async function deletePenalty(penaltyId) {
  const response = await fetchAPI(
    `/executive/penalties/${penaltyId}`,
    METHODS.delete
  );
  return parseBody(response);
}

/**
 * Get all inspections with penalty recommendation status from all organizations
 * @param {Object} params - Query parameters
 * @param {Date} [params.startDate] - Filter by start date (ISO string)
 * @param {Date} [params.endDate] - Filter by end date (ISO string)
 * @param {number} [params.page] - Page number for pagination
 * @param {number} [params.pageSize] - Number of items per page
 * @param {Object} [params.filterModel] - Filter model for filtering inspections
 * @returns {Promise<Object>} - Object with inspections data and total count
 */
export async function getAllPenaltyRecommendedInspections(params = {}) {
  const { filterModel, ...queryParams } = params;

  const response = await fetchAPI(
    `/executive/inspections/penalty-recommended?${qs.stringify(queryParams)}`,
    METHODS.post,
    { filterModel }
  );
  const data = await parseBody(response);
  return data || { data: [], total: 0 };
}

/**
 * Get organization activities
 * @param {string} teamId - Organization team ID
 * @param {object} options - Options for pagination
 * @param {number} [options.page=0] - Page number
 * @param {number} [options.pageSize=10] - Page size
 * @param {object} [options.filterModel] - Filter model for server-side filtering
 * @returns {Promise<{activities: Array, pagination: Object}>}
 */
export async function getOrganizationActivities(
  teamId,
  { page = 0, pageSize = 10, filterModel = null } = {}
) {
  const res = await fetchAPI(
    `/executive/organizations/${teamId}/activities?${qs.stringify({
      page,
      pageSize,
    })}`,
    METHODS.post,
    { filterModel }
  );
  return parseBody(res);
}

/**
 * Get all activities for every organization
 * @param {object} options - Options for pagination
 * @param {number} [options.page=0] - Page number
 * @param {number} [options.pageSize=10] - Page size
 * @param {object} [options.filterModel] - Filter model for server-side filtering
 * @returns {Promise<{activities: Array, pagination: Object}>}
 */
export async function getAllActivities({
  page = 0,
  pageSize = 10,
  filterModel = null,
} = {}) {
  const res = await fetchAPI(
    `/executive/organization-activities?${qs.stringify({
      page,
      pageSize,
    })}`,
    METHODS.post,
    { filterModel }
  );
  return parseBody(res);
}

/**
 * Get all activities for an organization with CSV export options
 * @param {string} teamId - Organization team ID
 * @param {object} options - Export options
 * @param {object} [options.filterModel] - Filter model for filtering
 * @returns {Promise<Array>} CSV data for export
 */
export async function exportOrganizationActivities(
  teamId,
  { filterModel = null } = {}
) {
  const res = await fetchAPI(
    `/executive/organizations/${teamId}/activities/export`,
    METHODS.post,
    { filterModel }
  );
  const { activities } = await parseBody(res);
  return activities.map((activity) => ({
    Timestamp: activity.created,
    'Activity Type': activity.activity_type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' '),
    User: activity.user_name,
  }));
}

/**
 * Logs a new activity for an organization.
 * @param {string} teamId Organization team ID
 * @param {string} type Activity type name
 */
export async function createOrganizationActivity(teamId, type) {
  const res = await fetchAPI(
    `/executive/organizations/${teamId}/activities/create`,
    METHODS.post,
    { type }
  );
  await assertErrors(res);
}

/**
 * Fetch preferred contacts for an organization.
 * @param {string} orgTeamId
 * @param {number} page
 * @param {number} pageSize
 * @param {object} filterModel - MUI DataGrid filter model
 * @returns Promise<{ contacts: PreferredContact[], count: number }>
 */
export async function fetchPreferredContacts(
  orgTeamId,
  page,
  pageSize,
  filterModel
) {
  const res = await fetchAPI(
    `/executive/preferred-contacts/${orgTeamId}?${qs.stringify({
      page,
      pageSize,
    })}`,
    METHODS.post,
    { filterModel }
  );
  const body = await parseBody(res);
  return body;
}

/**
 * Add a preferred contact for an organization.
 * @param {Object} params
 * @param {string} params.id - The ID of the preferred contact.
 * @param {string} params.orgTeamId - The organization team ID associated with the preferred contact.
 * @param {string} params.name - The name of the preferred contact.
 * @param {string} params.title - The title of the preferred contact.
 * @param {string} params.phone - The phone number of the preferred contact.
 * @param {string} params.email - The email address of the preferred contact.
 */
export async function addPreferredContact({
  id,
  orgTeamId,
  name,
  title,
  phone,
  email,
}) {
  const res = await fetchAPI(`/executive/preferred-contacts`, METHODS.post, {
    id,
    orgTeamId,
    name,
    title,
    phone,
    email,
  });
  await assertErrors(res);
}

/**
 * Update a preferred contact.
 * @param {Object} params
 * @param {string} params.id - The ID of the preferred contact.
 * @param {string} params.name - The name of the preferred contact.
 * @param {string} params.title - The title of the preferred contact.
 * @param {string} params.phone - The phone number of the preferred contact.
 * @param {string} params.email - The email address of the preferred contact.
 */
export async function updatePreferredContact({
  id,
  name,
  title,
  phone,
  email,
}) {
  const res = await fetchAPI(
    `/executive/preferred-contacts/${id}`,
    METHODS.put,
    { name, title, phone, email }
  );
  await assertErrors(res);
}

/**
 * Delete a preferred contact.
 * @param {string} id - The ID of the preferred contact.
 */
export async function deletePreferredContact(id) {
  const res = await fetchAPI(
    `/executive/preferred-contacts/${id}`,
    METHODS.delete
  );
  await assertErrors(res);
}
